import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";

export default function MentorRequestsPage() {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteFor, setNoteFor] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/mentor/idea-requests");
      setReqs(r.data);
    } catch (e) {
      toast.error("Could not load requests");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    setBusy(id);
    try {
      await api.post(`/mentor/idea-requests/${id}/decide`, { decision, note: noteFor[id] || "" });
      toast.success(decision === "accept" ? "Project accepted — workflow started" : "Declined — admin can re-route to alternate mentor");
      await load();
    } catch (e) {
      toast.error("Decision failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="mentor-requests-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">INCOMING REQUESTS</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Mentor decisions <em className="italic text-kb-primary">queue</em></h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Each request was forwarded by an admin after they reviewed the idea's feasibility. Accept to start mentoring this project — milestones, the 11-stage workflow and chat will be scaffolded automatically. Decline, and admin can route to an alternate mentor.
      </p>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        reqs.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No requests right now.</div>
        ) : (
          <div className="mt-8 space-y-4">
            {reqs.map((r) => {
              const idea = r.idea || {};
              const isPending = r.status === "pending_mentor";
              return (
                <div key={r.id} className="kb-card p-6" data-testid={`mentor-request-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{r.id} · {new Date(r.sent_at).toLocaleDateString()}</div>
                      <h3 className="font-serif text-xl text-kb-ink mt-1">{r.idea_title}</h3>
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">FROM {r.client_name}</div>
                      {idea.abstract && (
                        <p className="text-sm text-kb-ink-muted mt-2 line-clamp-3">{idea.abstract}</p>
                      )}
                      {idea.domain && (
                        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-2">
                          {idea.domain} · feasibility {idea.feasibility_score || "—"} · priority {idea.priority || "—"}
                        </div>
                      )}
                      {r.admin_note && <p className="text-sm text-kb-ink-muted mt-2"><span className="kb-mono text-[10px] text-kb-ink-light mr-2">ADMIN NOTE:</span>{r.admin_note}</p>}
                    </div>
                    <span className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border ${
                      r.status === "accepted" ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                      r.status === "rejected" ? "border-rose-200 bg-rose-50 text-rose-700" :
                      "border-amber-200 bg-amber-50 text-amber-700"
                    }`}>{r.status === "pending_mentor" ? "awaiting decision" : r.status}</span>
                  </div>

                  {isPending && (
                    <div className="mt-5 pt-5 border-t border-slate-200">
                      <textarea
                        rows={2}
                        className="kb-input resize-none text-sm"
                        placeholder="Add a private note for the admin / client (optional)…"
                        value={noteFor[r.id] || ""}
                        onChange={(e) => setNoteFor({ ...noteFor, [r.id]: e.target.value })}
                        data-testid={`mentor-request-${r.id}-note`}
                      />
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => decide(r.id, "accept")}
                          disabled={busy === r.id}
                          className="kb-btn-primary text-sm"
                          data-testid={`mentor-request-${r.id}-accept`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Accept project
                        </button>
                        <button
                          onClick={() => decide(r.id, "reject")}
                          disabled={busy === r.id}
                          className="kb-btn-outline text-sm"
                          data-testid={`mentor-request-${r.id}-reject`}
                        >
                          <XCircle className="w-3.5 h-3.5" /> Decline
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
